import { useEffect, useState, useMemo } from 'react';
import { Row, Col, Form, Input, Select, Collapse } from 'antd';
import { useConfig } from "../../../../contexts/ConfigContext";
import { COUNTRY_CODES } from "../../../../constants/countries";
import { getIdentityDocument } from "../../../../constants/identityDocuments";

export const BillingInfoSectionSlot = ({ selectedClient, tpv, children }) => {
    const { country } = useConfig();
    const form = Form.useFormInstance();
    const [activeKeys, setActiveKeys] = useState([]);

    const isHonduras = useMemo(() => country?.code === COUNTRY_CODES.HONDURAS, [country]);
    const isSalvador = useMemo(() => country?.code === COUNTRY_CODES.ELSALVADOR, [country]);
    const salvadorDocuments = useMemo(
        () => getIdentityDocument(COUNTRY_CODES.ELSALVADOR),
        []
    );
    const fiscalInvoiceType = Form.useWatch("Fiscal_Invoice_Type", form);
    const tpvBillingDefaults = useMemo(() => {
        return {
            code: tpv?.No || "",
            name: tpv?.nombre || "",
            address: tpv?.direccion || "",
            city: tpv?.ciudad || "",
            country: tpv?.pais || "",
            phone: tpv?.telefono || "",
            email: tpv?.email || "",
        };
    }, [tpv]);

    const applySalvadorTPVDefaults = () => {
        if (!isSalvador || !tpv || !form) return;
        const tpvCountry = String(tpvBillingDefaults.country || "").trim().toUpperCase();

        if (tpvCountry && tpvCountry !== COUNTRY_CODES.ELSALVADOR) {
            if (typeof window !== "undefined" && window.messageApi) {
                window.messageApi.warning(
                `El TPV activo (${tpvBillingDefaults.code || tpvBillingDefaults.name || "sin código"}) no es de El Salvador (${tpvCountry}).`
                );
            }
            return;
        }

        form.setFieldsValue({
            Bill_to_Address: tpvBillingDefaults.address,
            Bill_to_City: tpvBillingDefaults.city,
            Bill_to_Country_Region_Code: tpvBillingDefaults.country || form.getFieldValue("Bill_to_Country_Region_Code") || "",
            Telefono: tpvBillingDefaults.phone,
            E_Mail: tpvBillingDefaults.email,
        });
    };

    useEffect(() => {
        if (selectedClient) {
            setActiveKeys(["billing-details"]);
        }
    }, [selectedClient]);

    const options = useMemo(() => {
        let opts = [
            { value: "RUC", label: "RUC" },
            { value: "Cedula", label: "Cédula" },
            { value: "Pasaporte", label: "Pasaporte" },
        ];

        if (isHonduras) {
            opts = [{ value: "RTN", label: "RTN" }];
        }
        if (isSalvador) {
            const fiscalCredit = fiscalInvoiceType === "Factura crédito fiscal";
            opts = fiscalCredit
                ? [
                    { value: "NIT", label: "NIT" },
                    { value: "DUI", label: "DUI" },
                ]
                : [
                    { value: "NIT", label: "NIT" },
                    { value: "DUI", label: "DUI" },
                    { value: "Otros", label: "Otros" },
                ];
        }
        return opts;
    }, [isHonduras, isSalvador, fiscalInvoiceType]);

    useEffect(() => {
        if (options.length === 1 && form) {
            const singleValue = options[0].value;

            form.setFieldValue("Tipo_Documento_SrI", singleValue);
        }
    }, [options, form]);

    useEffect(() => {
        if (!isSalvador) return;
        const selectedType = form.getFieldValue("Tipo_Documento_SrI");
        const isAllowed = options.some((option) => option.value === selectedType);
        if (!isAllowed) {
            form.setFieldValue("Tipo_Documento_SrI", "NIT");
        }
    }, [form, isSalvador, options]);

    useEffect(() => {
        if (!isSalvador) return;
        if (fiscalInvoiceType === "Factura crédito fiscal") {
            return;
        }
        form.setFieldValue("NCR", "");
    }, [fiscalInvoiceType, form, isSalvador]);

    return (
        <Collapse
            style={{ marginTop: 16 }}
            activeKey={activeKeys}
            onChange={setActiveKeys}
            items={[
                {
                    key: "billing-details",
                    label: "Datos de facturación detallados",
                    children: (
                        <>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="Bill_to_Name"
                                        label="Nombre de facturación"
                                        rules={[{ required: true, message: "Ingrese el nombre del cliente" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="Bill_to_Address"
                                        label="Dirección de facturación"
                                        rules={[{ required: true, message: "Ingrese una dirección de facturación" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="Direccion_2" label="Envio a Colonia 2">
                                        <Input placeholder="Dirección secundaria o información adicional de envío" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="Bill_to_City"
                                        label="Ciudad"
                                        rules={[{ required: true, message: "Ingrese una ciudad" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        name="Tipo_Documento_SrI"
                                        label="Tipo Documento"
                                        rules={[{ required: true, message: "Seleccione el tipo de documento" }]}
                                    >
                                        <Select
                                            placeholder="Tipo"
                                            options={options}
                                            disabled={options.length === 1}
                                            onChange={() => applySalvadorTPVDefaults()}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={20}>
                                    <Form.Item
                                        name="VAT_Registration_No_"
                                        label="Número de identificación"
                                        rules={[
                                            { required: true, message: "Ingrese el número de identificación" },
                                            {
                                                validator: (_, value) => {
                                                    const selectedType = form.getFieldValue("Tipo_Documento_SrI");

                                                    if (isHonduras && value && value.length > 0) {
                                                        if (value.length !== 14) {
                                                            return Promise.reject(new Error("El RTN debe tener 14 caracteres"));
                                                        }
                                                    }

                                                    if (isSalvador && value && value.length > 0) {
                                                        const selectedDoc = Object.values(salvadorDocuments)
                                                            .find((doc) => doc.value === selectedType);
                                                        if (selectedDoc && !selectedDoc.regex.test(value)) {
                                                            return Promise.reject(new Error(selectedDoc.message));
                                                        }
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        name="Bill_to_Country_Region_Code"
                                        label="País"
                                        style={{ display: "none" }}
                                    >
                                        <Input disabled={true} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="Telefono"
                                        label="Teléfono de facturación"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="E_Mail"
                                        label="Email de facturación"
                                        rules={[
                                            { type: "email", message: "Formato de email inválido" },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {/* Country-specific extra fields*/}
                            {children}
                        </>
                    ),
                },
            ]}
        />
    );
};